export default function TermsContent() {
  return (
    <>
      <p>
        <strong>Last Updated</strong>: September 2, 2026
      </p>

      <p>
        These Terms of Use (the &ldquo;<strong>Terms</strong>&rdquo;) govern
        access to and use of the Lutely website, applications, software,
        marketplace, artificial-intelligence and audio-processing tools, and
        related services (collectively, the &ldquo;<strong>Platform</strong>
        &rdquo;) offered by License Lounge, Inc., a Delaware corporation doing
        business as Lutely (&ldquo;<strong>Lutely</strong>,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
      </p>

      <p>
        By creating an account, clicking to accept these Terms, purchasing
        anything through the Platform, uploading or publishing content,
        connecting a payment account, or otherwise using the Platform, you agree
        to these Terms and the policies incorporated by reference, including our{" "}
        <a href="/privacy">Privacy Policy</a>,{" "}
        <a href="/acceptable-use">Acceptable Use Policy</a>, and{" "}
        <a href="/dmca">Digital Millennium Copyright Act Policy</a>. If you do
        not agree, do not use the Platform.
      </p>

      <h2>1. Eligibility and authority</h2>
      <p>
        You must be at least thirteen (13) years old to use the Platform as a
        listener or buyer. If you are under eighteen (18), you may use the
        Platform only with the permission and supervision of a parent or legal
        guardian, who is responsible for your use and purchases.
      </p>
      <p>
        You must be at least eighteen (18) years old and legally able to enter
        into a binding contract to publish or sell music, connect a Stripe
        account, receive payments, or act as an Artist. An entity, label,
        manager, distributor, or other organization may use the Platform only
        through an individual authorized to bind that organization and the
        artists or rights holders it represents.
      </p>
      <p>
        You represent that all information you provide is accurate, current, and
        complete and that you have authority to accept these Terms for yourself
        and any person or entity on whose behalf you act.
      </p>

      <h2>2. Accounts and account security</h2>
      <p>
        Some features require an account. You are responsible for safeguarding
        your login credentials, maintaining accurate account information, and
        all activity occurring through your account. You may not sell, transfer,
        share, or permit another person to use your account except that an
        Artist may authorize employees, managers, agents, or contractors to
        assist with the Artist account under the Artist&rsquo;s supervision.
      </p>
      <p>
        Notify Lutely promptly at{" "}
        <a href="mailto:support@lutely.com">support@lutely.com</a> if you
        believe an account has been compromised. Lutely may require identity,
        authority, rights-ownership, tax, or payment verification before
        permitting access to features or releasing funds.
      </p>
      <p>
        You do not acquire an ownership interest in your account. Lutely may
        reject, suspend, restrict, or terminate accounts as these Terms provide.
      </p>

      <h2>3. Platform roles and definitions</h2>
      <p>
        A &ldquo;<strong>User</strong>&rdquo; is any person or entity that
        accesses or uses the Platform.
      </p>
      <p>
        An &ldquo;<strong>Artist</strong>&rdquo; or &ldquo;
        <strong>Seller</strong>&rdquo; is a User that uploads, processes,
        publishes, offers, or sells Artist Content. An Artist may be an
        individual performer, band, record label, distributor, manager, rights
        holder, or other authorized entity.
      </p>
      <p>
        A &ldquo;<strong>Listener</strong>&rdquo; or &ldquo;
        <strong>Buyer</strong>&rdquo; is a User that browses, streams,
        downloads, or purchases Artist Content.
      </p>
      <p>
        &ldquo;<strong>Artist Content</strong>&rdquo; means sound recordings,
        musical compositions, albums, tracks, audiovisual works, artwork,
        photographs, names, likenesses, biographies, lyrics, metadata,
        descriptions, pricing, promotional materials, and other materials an
        Artist submits to or makes available through the Platform.
      </p>
      <p>
        &ldquo;<strong>User Content</strong>&rdquo; means Artist Content and any
        other material a User submits, posts, sends, or makes available through
        the Platform, including profile information, comments, messages,
        prompts, and feedback.
      </p>
      <p>
        &ldquo;<strong>Lutely Tools</strong>&rdquo; means the Platform&rsquo;s
        audio, video, data, artificial-intelligence, workflow, content-analysis,
        asset-management, and other creation or processing tools.
      </p>
      <p>
        &ldquo;<strong>Output</strong>&rdquo; means any audio, video, text,
        metadata, analysis, derivative file, visual, or other material produced
        through a Lutely Tool from User Content or a User&rsquo;s instructions.
      </p>

      <h2>4. Artist marketplace and merchant-of-record structure</h2>
      <p>
        The Platform enables Artists to offer digital music directly to Buyers
        and enables Buyers to place a single order containing music from one or
        multiple Artists.
      </p>
      <p>
        For payment-processing purposes, Lutely is the merchant of record and
        the Buyer&rsquo;s payment counterparty. The Buyer&rsquo;s payment is
        charged by Lutely, and Lutely may appear on card statements, payment
        confirmations, and receipts. Lutely may collect the entire order amount,
        deduct applicable amounts, and transfer Artist Proceeds to one or more
        Artists through Stripe Connect or another payment provider.
      </p>
      <p>
        Lutely does not acquire ownership of Artist Content merely because
        Lutely processes a sale or acts as merchant of record. The applicable
        Artist remains the owner, authorized controller, supplier, and licensor
        of the Artist Content and is responsible for its legality, accuracy,
        quality, and rights clearance.
      </p>
      <p>
        A marketplace order may include Artist Content from multiple Artists.
        Lutely may allocate the order price, fees, taxes, refunds, disputes,
        chargebacks, and other adjustments among the applicable Artists using
        commercially reasonable methods.
      </p>

      <h2>5. Publishing and Stripe Connect</h2>
      <p>
        An Artist must successfully complete Stripe Connect onboarding and
        maintain an eligible connected account before publishing Artist Content
        for sale. Stripe&rsquo;s services are governed by Stripe&rsquo;s own
        agreements and privacy policy. Lutely does not control Stripe&rsquo;s
        eligibility, verification, reserve, payout, or account-closure
        decisions.
      </p>
      <p>
        Artists authorize Lutely and Stripe to create charges, collect payments,
        deduct amounts, make transfers, reverse transfers, debit available
        balances where permitted, and take other actions reasonably necessary to
        administer marketplace transactions.
      </p>
      <p>
        Lutely may require additional documentation showing identity, authority,
        ownership, licenses, label relationships, contributor permissions, tax
        status, or the source of Artist Content. Lutely may decline or remove a
        listing or suspend payouts until requested information is provided to
        Lutely&rsquo;s satisfaction.
      </p>

      <h2>6. Prices, service fees, commissions, and processing costs</h2>
      <p>
        Artists generally set the listed price for their Artist Content, subject
        to minimum prices, maximum prices, supported currencies, and other
        pricing rules established by Lutely. Lutely may require a minimum price
        to ensure that payment-processing costs and other transaction expenses
        do not consume the full sale amount.
      </p>
      <p>
        Lutely currently does not deduct a sales commission from the
        Artist&rsquo;s listed price. This is referred to as
        &ldquo;commission-free&rdquo; selling. Commission-free does not mean
        transactions have no costs. Payment-processing fees, taxes, refunds,
        disputes, chargebacks, reserves, adjustments, and other amounts
        described in these Terms may be deducted from Artist Proceeds.
      </p>
      <p>
        Lutely currently charges Buyers a service fee equal to the greater of
        one U.S. dollar ($1.00) or six percent (6%) of the applicable purchase
        amount. The service fee and total amount charged will be disclosed
        before checkout is completed. Stripe calculates its processing fee using
        the entire amount charged to the Buyer, including Lutely&rsquo;s service
        fee. Lutely deducts the full applicable Stripe processing fee when
        calculating Artist Proceeds, so the Artist may bear a small additional
        processing cost attributable to Lutely&rsquo;s service fee.
      </p>
      <p>
        Lutely may change pricing rules, service fees, commissions,
        processing-fee allocation, payout methods, or other charges
        prospectively. Material changes affecting an Artist&rsquo;s economics
        will be disclosed through the Platform, by email, or through another
        reasonable notice method before they apply to future sales.
      </p>

      <h2>7. Artist Proceeds and payout calculations</h2>
      <p>
        &ldquo;<strong>Artist Proceeds</strong>&rdquo; means the amount Lutely
        calculates as payable to an Artist for a transaction after deducting or
        allocating, as applicable:
      </p>
      <ul>
        <li>payment-processing and currency-conversion fees;</li>
        <li>
          refunds, credits, reversals, disputes, chargebacks, and related fees;
        </li>
        <li>
          taxes, tax withholding, levies, or reporting-related adjustments;
        </li>
        <li>reserves, holds, offsets, and negative balances;</li>
        <li>
          amounts attributable to fraud, infringement, manipulation, errors, or
          violations of these Terms;
        </li>
        <li>amounts the Artist owes Lutely or another User; and</li>
        <li>
          any other fee or deduction clearly disclosed through the Platform or
          agreed with the Artist.
        </li>
      </ul>
      <p>
        Lutely&rsquo;s dashboard calculations, estimates, analytics, and reports
        may be delayed or contain errors. Lutely may correct calculation,
        allocation, or payment errors and may adjust future Artist Proceeds
        accordingly.
      </p>
      <p>
        Artists are solely responsible for agreements with collaborators,
        featured artists, producers, songwriters, publishers, labels,
        distributors, managers, unions, guilds, and other participants. Lutely
        has no obligation to divide Artist Proceeds among those persons unless
        Lutely expressly offers and the Artist uses a supported split-payment
        feature.
      </p>

      <h2>8. Payout timing, reserves, holds, reversals, and recovery</h2>
      <p>
        Subject to any holds, reserves, reversals, offsets, and other rights
        described in this Section or otherwise communicated by Lutely to
        Artists, Lutely may make Artist Proceeds available on a monthly payout
        basis, or on a more frequent payout basis depending on the applicable
        Artist account type or plan.
      </p>
      <p>
        Lutely may change payout schedules, eligibility, methods, or fees
        prospectively. The availability of proceeds under any standard schedule
        remains subject to the protections and adjustments described below.
      </p>
      <p>
        Payout timing shown on the Platform is an estimate and is not
        guaranteed. Lutely may delay, suspend, withhold, reserve, reverse,
        recoup, or offset any transfer or payout when Lutely reasonably believes
        doing so is necessary to:
      </p>
      <ul>
        <li>
          process or anticipate a refund, dispute, chargeback, reversal, or
          processing fee;
        </li>
        <li>
          investigate suspected fraud, unauthorized activity, stolen or
          infringing content, artificial sales, or other misconduct;
        </li>
        <li>address unusually high refund, dispute, or chargeback rates;</li>
        <li>
          comply with law, a court order, a payment-provider requirement, or a
          rights-holder request;
        </li>
        <li>correct an error or duplicate payment;</li>
        <li>
          protect Lutely, Buyers, Artists, payment providers, or third parties
          from loss; or
        </li>
        <li>collect an amount the Artist owes under these Terms.</li>
      </ul>
      <p>
        Lutely may establish transaction-specific or rolling reserves and may
        apply longer payout delays to new, high-risk, or restricted accounts.
        Lutely is not required to disclose confidential fraud-detection methods.
      </p>
      <p>
        If Lutely refunds or credits a Buyer, loses a dispute, pays a chargeback
        or related fee, or otherwise incurs a loss attributable to an Artist,
        the Artist authorizes Lutely to reverse the corresponding transfer,
        debit available balances where permitted, deduct the amount from future
        Artist Proceeds, invoice the Artist, or use any other lawful collection
        method.
      </p>
      <p>
        An Artist remains responsible for amounts owed after account suspension,
        termination, or disconnection from Stripe. If the Artist&rsquo;s
        available balance is insufficient, the Artist must reimburse Lutely
        promptly upon demand.
      </p>

      <h2>9. Purchases, digital licenses, downloads, and streaming</h2>
      <p>
        When a Buyer purchases digital music, the Buyer receives a limited,
        personal, non-exclusive, non-transferable, non-sublicensable license to
        download, store, and listen to the purchased files for personal,
        non-commercial use. The purchase does not transfer ownership of the
        sound recording, musical composition, artwork, trademarks, publicity
        rights, or other intellectual property.
      </p>
      <p>
        Subject to these Terms, Platform availability, and legal restrictions, a
        Buyer may stream purchased music through the Buyer&rsquo;s account and
        may redownload the purchased files. Buyers may not redistribute, resell,
        publicly perform, broadcast, upload, share, sublicense, commercially
        exploit, alter rights-management information, circumvent access
        controls, or use purchased files to train artificial-intelligence
        systems without authorization from the applicable rights holders.
      </p>
      <p>
        Lutely may make different file formats or quality levels available and
        may create delivery files from source files uploaded by the Artist.
        Information shown at checkout or on the purchase page will govern the
        file format delivered.
      </p>
      <p>
        The Buyer is responsible for maintaining independent backups of
        downloaded files. Lutely does not guarantee that streaming, account
        access, or redownload functionality will remain available permanently.
      </p>

      <h2>10. Final sales and refunds</h2>
      <p>
        Digital music purchases are final and nonrefundable except where
        required by law or where Lutely, in its discretion, determines that a
        refund, credit, or other remedy is appropriate. Circumstances that may
        support a remedy include a duplicate or unauthorized charge, failure to
        deliver the purchased content, a materially corrupted file, or removal
        of content because it was unauthorized or infringing.
      </p>
      <p>
        Refund requests must be submitted to Lutely through the support methods
        provided on the Platform.
      </p>
      <p>
        A Buyer should contact Lutely before initiating a dispute with a bank or
        payment provider so that Lutely has an opportunity to investigate and
        resolve the issue. Lutely may suspend or terminate accounts associated
        with fraudulent, abusive, or bad-faith disputes and may recover
        resulting losses where permitted by law.
      </p>

      <h2>11. Removal of Artist Content and prior purchases</h2>
      <p>
        An Artist may stop offering Artist Content for new purchases by using
        the available Platform controls. Removing a listing of Artist Content
        from the Platform does not automatically terminate licenses already
        granted to Buyers.
      </p>
      <p>
        The Artist authorizes Lutely to retain, host, reproduce, stream, and
        redistribute previously purchased Artist Content as reasonably necessary
        to provide continuing access and replacement downloads to valid prior
        Buyers. This authorization survives removal of the listing and
        termination of the Artist&rsquo;s account for as long as Lutely
        continues to provide access to valid prior Buyers.
      </p>
      <p>
        Lutely may disable access to purchased content where Lutely reasonably
        believes continued access would violate law, a court order, a
        rights-holder demand, the DMCA, a payment-provider rule, or a third
        party&rsquo;s rights. Lutely may also discontinue access because of
        service shutdown, security risk, technical impossibility, or
        circumstances outside Lutely&rsquo;s reasonable control. Where
        appropriate, Lutely may provide a replacement, credit, or refund, but
        does not guarantee that purchased content will remain available forever.
      </p>

      <h2>12. Artist ownership and license to Lutely</h2>
      <p>
        As between Lutely and the Artist, the Artist retains ownership of Artist
        Content and Outputs to the extent the Artist owns the underlying rights
        and applicable law permits ownership.
      </p>
      <p>
        The Artist grants Lutely and its service providers a worldwide,
        non-exclusive, royalty-free, fully paid, sublicensable license to host,
        store, reproduce, copy, format, transcode, modify for technical
        purposes, create previews and delivery versions, distribute, transmit,
        publicly perform, publicly display, communicate, market, promote, and
        otherwise use Artist Content and Outputs as reasonably necessary to:
      </p>
      <ul>
        <li>
          operate, secure, improve, test, and provide the Platform and Lutely
          Tools;
        </li>
        <li>
          create, process, store, and deliver Outputs requested by the Artist;
        </li>
        <li>display Artist pages and listings;</li>
        <li>stream, preview, sell, and deliver Artist Content to Buyers;</li>
        <li>
          market Lutely, the Platform, the Artist, and available releases; and
        </li>
        <li>
          comply with law, enforce these Terms, and respond to rights claims.
        </li>
      </ul>
      <p>
        The Artist grants Lutely and its service providers a worldwide,
        non-exclusive, royalty-free, fully paid, sublicensable license to use
        the Artist&rsquo;s names, trademarks, logos, likenesses, biographies,
        artwork, and promotional materials in connection with the Platform and
        promotion of the Artist&rsquo;s presence on Lutely.
      </p>
      <p>
        The license continues for as long as necessary to provide the Platform,
        administer transactions, maintain records, support prior purchases,
        resolve disputes, and comply with law. Rights needed to support valid
        prior purchases survive account termination and content removal.
      </p>

      <h2>13. Lutely Tools, artificial intelligence, and Outputs</h2>
      <p>
        Lutely Tools may analyze, transform, edit, separate, censor, transcode,
        summarize, classify, generate, or otherwise process User Content.
        Outputs may include stems, clean versions, lyrics, metadata, content
        analyses, videos, visual assets, or other materials.
      </p>
      <p>
        Users are responsible for reviewing Outputs before use or publication.
        Outputs may contain errors, omissions, artifacts, misclassifications, or
        similarities to other material. Lutely does not guarantee that an Output
        is accurate, unique, noninfringing, commercially suitable, or eligible
        for copyright or other protection.
      </p>
      <p>
        To provide, operate, test, secure, maintain, develop, train, evaluate,
        and improve Lutely and the Lutely Tools, the User grants Lutely a
        worldwide, non-exclusive, royalty-free, fully paid-up, sublicensable
        license, during and permanently after the User&rsquo;s use of the
        Platform, to store, reproduce, process, modify, analyze, and create
        derivative works from User Content, prompts, usage data, and Outputs,
        and to use those materials to train, test, evaluate, and improve
        Lutely&rsquo;s artificial-intelligence models, tools, systems, and
        related technology. Lutely may exercise these rights for Lutely&rsquo;s
        products, services, safety, security, research, operations, and
        product-improvement purposes, as further described in the{" "}
        <a href="/privacy">Privacy Policy</a>. Lutely does not acquire ownership
        of Artist Content under this license. Lutely will not use Artist Content
        to create or market an unauthorized clone of an Artist&rsquo;s voice or
        distinctive sound.
      </p>
      <p>
        A User may not use Lutely Tools to impersonate a person without
        authorization; create deceptive, unlawful, defamatory, exploitative, or
        infringing material; remove rights-management information; circumvent
        access controls; or falsely represent artificially generated material as
        an authentic performance by another person.
      </p>
      <p>
        <strong>Disclaimer.</strong> Lutely Tools may produce Outputs that are
        automated, probabilistic, incomplete, inaccurate, inconsistent, or
        unsuitable for a particular purpose. Outputs may not be unique and may
        be similar to material generated for other users. Lutely does not
        guarantee the accuracy, quality, availability, non-infringement,
        ownership, commercial suitability, or legal status of any Output. You
        are responsible for reviewing and evaluating Outputs before using,
        publishing, distributing, licensing, relying on, or making decisions
        based on them, including confirming that your use does not violate
        applicable law or infringe, misappropriate, or otherwise violate the
        rights of any person. In particular, you are responsible for obtaining
        all rights, permissions, clearances, consents, and licenses necessary
        for your prompts, User Content, and intended use of any Output,
        including rights relating to copyrights, sound recordings, musical
        compositions, names, likenesses, voices, publicity, privacy, and
        trademarks.
      </p>

      <h2>14. Artist representations, warranties, and obligations</h2>
      <p>Each Artist represents, warrants, and agrees that:</p>
      <ul>
        <li>
          the Artist owns or controls all rights necessary to upload, process,
          publish, sell, stream, promote, and deliver the Artist Content, use
          the Lutely Tools, and to grant the rights in these Terms;
        </li>
        <li>
          all sound-recording, composition, publishing, mechanical, performance,
          synchronization, artwork, trademark, name, likeness, privacy,
          publicity, sample, interpolation, and other necessary rights and
          permissions have been obtained;
        </li>
        <li>
          the Artist has authority to act for every artist, label, distributor,
          rights holder, and contributor identified in the account or metadata;
        </li>
        <li>
          Artist Content, metadata, credits, descriptions, prices, and account
          information are accurate and not misleading;
        </li>
        <li>
          Artist Content and its exploitation through the Platform will not
          infringe, misappropriate, or violate any third party&rsquo;s rights or
          any law;
        </li>
        <li>
          the Artist will not upload unauthorized cover recordings, samples,
          interpolations, remixes, impersonations, or other derivative material;
        </li>
        <li>
          no undisclosed payment, royalty, fee, residual, union payment, guild
          payment, or other compensation will become payable by Lutely because
          of the Artist Content or its exploitation as authorized by these
          Terms;
        </li>
        <li>
          the Artist will not manipulate sales, make sham purchases, use stolen
          payment methods, coordinate fraudulent transactions, or attempt to
          influence charts or metrics deceptively;
        </li>
        <li>
          the Artist will respond promptly to Lutely&rsquo;s requests for rights
          documentation or transaction information; and
        </li>
        <li>
          the Artist will comply with all laws applicable to the Artist&rsquo;s
          business, content, communications, taxes, and use of Buyer
          information.
        </li>
      </ul>
      <p>
        The Artist must notify Lutely promptly if any relevant right, license,
        authority, or permission expires, is revoked, becomes disputed, or
        changes in a way that affects Artist Content.
      </p>

      <h2>15. Buyer information and Artist communications</h2>
      <p>
        For each purchase, Lutely may provide the applicable Artist with
        information about the Buyer and transaction, including the Buyer&rsquo;s
        name, email address, purchase amount, purchased items, and related
        transaction details. The Privacy Policy explains these disclosures.
      </p>
      <p>
        An Artist may use Buyer information only for legitimate purposes related
        to the Artist&rsquo;s relationship with that Buyer, including customer
        service, purchase support, release news, artist updates, and lawful
        direct communications. The Artist may not sell, rent, license, exchange,
        disclose, or provide Buyer information to unrelated third parties or use
        it for unlawful discrimination, harassment, fraud, identity theft, data
        brokerage, or unrelated profiling.
      </p>
      <p>
        Every Artist communication must accurately identify the sender, comply
        with applicable email, text-message, privacy, and consumer-protection
        laws, and provide a functional method to unsubscribe or opt out where
        required. An Artist must honor opt-out requests promptly and must not
        continue promotional communications after a Buyer has opted out.
      </p>
      <p>
        The Artist is independently responsible for copies of Buyer information
        exported, downloaded, or stored outside Lutely. Upon request, account
        termination, or when the information is no longer needed, the Artist
        must delete or restrict its use as required by law and Lutely&rsquo;s
        instructions, except to the extent retention is legally required.
      </p>
      <p>
        Lutely may provide in-Platform messaging or communication features.
        Users may communicate freely only in compliance with these Terms and the
        Acceptable Use Policy. Lutely may investigate abuse, but does not
        guarantee that it will monitor or preserve every communication.
      </p>

      <h2>16. Taxes and reporting</h2>
      <p>
        Artists are responsible for income, franchise, business, payroll,
        royalty, and other taxes imposed on the Artist or the Artist&rsquo;s
        earnings, personnel, or business activities.
      </p>
      <p>
        Lutely may calculate, collect, withhold, report, and remit sales tax,
        use tax, value-added tax, goods-and-services tax, withholding tax, or
        other taxes where Lutely determines that it is required or appropriate
        to do so. Lutely may request tax forms, taxpayer-identification numbers,
        certifications, or other information and may withhold payouts until
        satisfactory information is provided.
      </p>
      <p>
        Lutely or Stripe may issue tax forms or transaction reports as required
        by law. Artists remain responsible for obtaining independent tax advice
        and filing accurate returns. Lutely does not provide legal, accounting,
        or tax advice.
      </p>

      <h2>17. Subscriptions and paid Platform services</h2>
      <p>
        Lutely may offer monthly, annual, usage-based, credit-based, or other
        paid plans and services. Prices, included features, usage limits, and
        billing periods will be disclosed before purchase.
      </p>
      <p>
        By purchasing a recurring subscription, you authorize Lutely or its
        payment provider to charge the applicable payment method at the start of
        each billing period until cancellation. Subscriptions automatically
        renew unless canceled before the next renewal date. Cancellation takes
        effect at the end of the current paid billing period unless the Platform
        states otherwise.
      </p>
      <p>
        Subscription and service fees are nonrefundable except where required by
        law or expressly stated by Lutely. Lutely may change subscription prices
        prospectively after reasonable notice. Lutely may modify or discontinue
        features and may impose or change reasonable usage limits.
      </p>

      <h2>18. Acceptable use and prohibited conduct</h2>
      <p>
        You must comply with the{" "}
        <a href="/acceptable-use">Acceptable Use Policy</a>. Without limiting
        that policy, you may not:
      </p>
      <ul>
        <li>violate law or another person&rsquo;s rights;</li>
        <li>
          upload, sell, distribute, or process content you do not have authority
          to use;
        </li>
        <li>impersonate another person, artist, label, or organization;</li>
        <li>
          engage in fraud, payment abuse, money laundering, sanctions evasion,
          wash trading, fake sales, chart manipulation, or deceptive activity;
        </li>
        <li>scrape, harvest, sell, or misuse personal information;</li>
        <li>send spam, threats, harassment, or unlawful communications;</li>
        <li>
          interfere with the Platform, security controls, payment systems, or
          other Users;
        </li>
        <li>
          reverse engineer or misuse Platform software except where applicable
          law prohibits that restriction; or
        </li>
        <li>
          use bots, automation, or high-volume access without Lutely&rsquo;s
          written permission.
        </li>
      </ul>
      <p>
        Lutely may remove content, cancel transactions, restrict features, or
        suspend accounts where Lutely reasonably suspects a violation.
      </p>

      <h2>19. Copyright complaints and repeat infringers</h2>
      <p>
        Lutely responds to copyright notices and counter-notices under its{" "}
        <a href="/dmca">Digital Millennium Copyright Act Policy</a>. Lutely may
        remove or disable access to Artist Content, suspend payouts, preserve
        funds, disclose information where legally permitted, and terminate
        repeat infringers.
      </p>
      <p>
        A copyright removal may affect both new sales and access by prior
        purchasers. Lutely will determine, in light of the claim and applicable
        law, whether prior-purchase access can continue, must be disabled, or
        may be restored.
      </p>

      <h2>20. Third-party services</h2>
      <p>
        The Platform may depend on Stripe, cloud-hosting providers, analytics
        providers, authentication providers, communications providers, and other
        third parties. Lutely does not use third-party AI model providers to
        process customer audio, prompts, or Outputs unless disclosed.
        Third-party services may have separate terms and privacy policies.
      </p>
      <p>
        Lutely is not responsible for third-party outages, eligibility
        decisions, account restrictions, security incidents, fees, or acts and
        omissions except to the extent responsibility cannot legally be
        disclaimed.
      </p>

      <h2>21. Suspension and termination</h2>
      <p>
        Lutely may suspend, restrict, or terminate access to all or part of the
        Platform immediately where Lutely reasonably believes a User has
        violated these Terms, created legal or financial risk, engaged in fraud
        or infringement, failed verification, accumulated a negative balance, or
        threatened the Platform or another person.
      </p>
      <p>
        Lutely may otherwise discontinue an account or feature upon reasonable
        notice where practicable. A User may close an account using available
        controls or by contacting support.
      </p>
      <p>
        Termination does not eliminate obligations or liabilities arising before
        termination. Provisions concerning ownership, licenses needed for prior
        purchases, payment adjustments, taxes, confidentiality, data use,
        warranties, disclaimers, limitation of liability, indemnification,
        disputes, and amounts owed survive.
      </p>

      <h2>22. Platform changes and availability</h2>
      <p>
        Lutely may add, remove, modify, suspend, or discontinue features, plans,
        file formats, pricing rules, integrations, and Platform functionality.
        Lutely does not guarantee uninterrupted or error-free availability.
      </p>
      <p>
        The Platform may be unavailable because of maintenance, third-party
        failures, cyberattacks, legal demands, disasters, or other
        circumstances. Lutely is not liable for unavailable features or lost
        access except to the extent required by law.
      </p>

      <h2>23. Disclaimers</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM, LUTELY TOOLS,
        ARTIST CONTENT, OUTPUTS, PURCHASES, STREAMING, DOWNLOADS, AND ALL
        RELATED SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
        AVAILABLE,&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS, IMPLIED, OR
        STATUTORY.
      </p>
      <p>
        LUTELY DISCLAIMS IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, QUIET ENJOYMENT,
        AND WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE.
      </p>
      <p>
        LUTELY DOES NOT WARRANT THAT THE PLATFORM OR ANY CONTENT OR OUTPUT WILL
        BE ACCURATE, COMPLETE, SECURE, AVAILABLE, ERROR-FREE, FREE OF HARMFUL
        CODE, OR SUITABLE FOR A PARTICULAR PURPOSE. ARTIST CONTENT IS PROVIDED
        BY ARTISTS, AND LUTELY DOES NOT INDEPENDENTLY VERIFY EVERY RIGHT, CLAIM,
        CREDIT, DESCRIPTION, OR FILE.
      </p>
      <p>
        Some jurisdictions do not allow certain disclaimers, so some of the
        above may not apply to you.
      </p>

      <h2>24. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, LUTELY AND ITS AFFILIATES,
        OFFICERS, DIRECTORS, EMPLOYEES, CONTRACTORS, AGENTS, LICENSORS, AND
        SERVICE PROVIDERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS,
        REVENUE, GOODWILL, BUSINESS, DATA, CONTENT, OR OPPORTUNITIES, ARISING
        OUT OF OR RELATING TO THE PLATFORM OR THESE TERMS, EVEN IF ADVISED OF
        THE POSSIBILITY.
      </p>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, LUTELY&rsquo;S TOTAL AGGREGATE
        LIABILITY ARISING OUT OF OR RELATING TO THE PLATFORM OR THESE TERMS WILL
        NOT EXCEED THE GREATER OF (A) THE AMOUNT THE CLAIMANT PAID DIRECTLY TO
        LUTELY FOR PLATFORM SERVICES DURING THE TWELVE MONTHS BEFORE THE EVENT
        GIVING RISE TO THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS ($100).
      </p>
      <p>
        These limitations do not apply where prohibited by law and do not limit
        liability that cannot legally be limited.
      </p>

      <h2>25. Indemnification</h2>
      <p>
        You will indemnify, defend, and hold harmless Lutely and its affiliates,
        officers, directors, employees, contractors, agents, licensors, and
        service providers from claims, demands, proceedings, damages, losses,
        liabilities, penalties, judgments, settlements, costs, and expenses,
        including reasonable attorneys&rsquo; fees, arising out of or relating
        to:
      </p>
      <ul>
        <li>your User Content, Artist Content, or Outputs;</li>
        <li>your breach of these Terms or another incorporated policy;</li>
        <li>
          your infringement, misappropriation, or violation of another
          person&rsquo;s rights;
        </li>
        <li>
          your fraud, misconduct, communications, taxes, business activities, or
          use of Buyer information;
        </li>
        <li>
          an Artist&rsquo;s failure to pay a collaborator, rights holder, tax
          authority, or other person;
        </li>
        <li>
          transactions, refunds, disputes, chargebacks, or losses attributable
          to your account or Artist Content; or
        </li>
        <li>your use of the Platform in violation of law.</li>
      </ul>
      <p>
        Lutely may control the defense and settlement of an indemnified matter.
        You may not settle a claim in a manner that admits liability by or
        imposes obligations on Lutely without Lutely&rsquo;s written consent.
      </p>

      <h2>26. Disputes, governing law, arbitration, and class-action waiver</h2>
      <p>
        Before filing a formal claim, you agree to send written notice
        describing the dispute to{" "}
        <a href="mailto:notices@lutely.com">notices@lutely.com</a> and allow
        thirty (30) days for an informal resolution attempt.
      </p>
      <p>
        These Terms and disputes relating to them are governed by Delaware law,
        without regard to conflict-of-law principles, except that the Federal
        Arbitration Act governs arbitration.
      </p>
      <p>
        Except for eligible small-claims matters and claims seeking temporary or
        injunctive relief for intellectual-property misuse, any dispute, claim,
        or controversy arising out of or relating to these Terms or the Platform
        will be resolved by binding individual arbitration administered by JAMS
        under its applicable rules. Arbitration will occur in the county and
        state where Lutely has its principal place of business unless the
        parties agree otherwise or applicable consumer law requires another
        location or remote proceeding.
      </p>
      <p>
        ARBITRATION WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS. CLASS,
        COLLECTIVE, CONSOLIDATED, MASS, OR REPRESENTATIVE ACTIONS AND
        ARBITRATIONS ARE NOT PERMITTED TO THE MAXIMUM EXTENT ALLOWED BY LAW.
        EACH PARTY WAIVES THE RIGHT TO A JURY TRIAL.
      </p>
      <p>
        A court of competent jurisdiction may determine the enforceability of
        this arbitration agreement and class-action waiver where applicable law
        requires.
      </p>

      <h2>27. Changes to these Terms</h2>
      <p>
        Lutely may update these Terms. If a change is material, Lutely may
        provide notice by email, through the Platform, or by another reasonable
        method. Unless a different date is stated, changes apply prospectively
        thirty (30) days after notice to existing Users and immediately to Users
        who first accept the revised Terms after publication.
      </p>
      <p>
        Continued use after the effective date of revised Terms constitutes
        acceptance where permitted by law. If you do not agree, you must stop
        using the Platform and cancel applicable subscriptions before the
        revised Terms take effect.
      </p>

      <h2>28. General provisions</h2>
      <p>
        The relationship between you and Lutely is that of independent
        contracting parties. These Terms do not create a partnership, joint
        venture, employment, agency, franchise, fiduciary, or exclusive
        relationship, except that Artists authorize Lutely to act as merchant of
        record and payment facilitator for marketplace transactions as expressly
        described.
      </p>
      <p>
        You may not assign these Terms without Lutely&rsquo;s written consent.
        Lutely may assign these Terms in connection with a merger, acquisition,
        financing, reorganization, sale of assets, or by operation of law.
      </p>
      <p>
        If a provision is unenforceable, it will be modified to the minimum
        extent necessary, and the remainder will remain effective. A waiver must
        be in writing and does not waive future breaches.
      </p>
      <p>
        These Terms and incorporated policies constitute the entire agreement
        concerning their subject matter and supersede prior terms relating to
        the Platform after the effective date. Existing obligations, purchases,
        and liabilities arising under earlier terms survive to the extent
        necessary to give them effect.
      </p>
      <p>
        Headings are for convenience only. &ldquo;Including&rdquo; means
        &ldquo;including without limitation.&rdquo; Electronic notices and
        signatures have the same effect as written ones where permitted by law.
      </p>

      <h2>29. Contact and notices</h2>
      <p>
        Questions and support requests may be sent to{" "}
        <a href="mailto:support@lutely.com">support@lutely.com</a> or{" "}
        <a href="mailto:info@lutely.com">info@lutely.com</a>.
      </p>
      <p>
        Formal legal notices to Lutely must be sent to{" "}
        <a href="mailto:notices@lutely.com">notices@lutely.com</a> and, if
        required by law, to:
      </p>
      <p>
        License Lounge, Inc. d/b/a Lutely 440 Burroughs Street, Suite 129,
        Detroit, Michigan 48202 United States
      </p>
      <p>
        Copyright notices must be submitted as described in Lutely&rsquo;s{" "}
        <a href="/dmca">Digital Millennium Copyright Act Policy</a>.
      </p>
    </>
  );
}
